import React, { useEffect, useState } from "react";
import { api, API, tokenStore } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtRoDateTime } from "../lib/dateFmt";
import { Wrench, Plus, Trash2 } from "lucide-react";

const photoUrl = (id) => `${API}/service-log/${id}/photo?token=${encodeURIComponent(tokenStore.get() || "")}`;

export default function ServiceLog() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [form, setForm] = useState({ entry_type: "echipament", title: "", serial: "", car_id: "", note: "" });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/service-log").then((r) => setRows(r.data)).catch((e) => {
    setRows([]);
    toast.error(e?.response?.data?.detail || "Eroare la încărcare");
  });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (open && cars.length === 0) {
      api.get("/cars").then((r) => setCars((r.data || []).filter((c) => !c.is_equipment && c.status !== "sold"))).catch(() => {});
    }
  }, [open, cars.length]);

  const submit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("entry_type", form.entry_type);
      if (form.title) fd.append("title", form.title);
      if (form.serial) fd.append("serial", form.serial);
      if (form.car_id) fd.append("car_id", form.car_id);
      if (form.note) fd.append("note", form.note);
      if (photo) fd.append("photo", photo);
      await api.post("/service-log", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Adăugat");
      setOpen(false);
      setForm({ entry_type: "echipament", title: "", serial: "", car_id: "", note: "" });
      setPhoto(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    await api.patch(`/service-log/${r.id}`, { status: r.status === "open" ? "resolved" : "open" });
    load();
  };
  const remove = async (r) => {
    if (!window.confirm("Ștergi această înregistrare?")) return;
    await api.delete(`/service-log/${r.id}`);
    load();
  };

  const isEq = form.entry_type === "echipament";
  return (
    <div className="max-w-4xl mx-auto pb-16" data-testid="service-log-page">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl p-2.5 shadow"><Wrench size={22} /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Echipamente în garanție / Autoturisme la service</h1>
          <p className="text-sm text-zinc-500">Jurnal cu poze, serii, numere de înmatriculare și notițe</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="service-log-add-btn" className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-md"><Plus size={16} className="mr-1" /> Adaugă</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="service-log-dialog">
            <DialogHeader><DialogTitle>Eveniment nou</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-2">
              {["echipament", "autoturism"].map((t) => (
                <button key={t} type="button" onClick={() => setForm({ ...form, entry_type: t })}
                  className={`px-2.5 py-2 rounded-md text-left border transition-all ${form.entry_type === t ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400" : "border-zinc-200 hover:border-zinc-400"}`}
                  data-testid={`service-log-type-${t}`}>
                  <div className="text-sm font-bold">{t === "echipament" ? "🛠 Echipament" : "🚗 Autoturism"}</div>
                  <div className="text-[10px] text-zinc-500">{t === "echipament" ? "dus în garanție" : "dus la service"}</div>
                </button>
              ))}
            </div>
            {isEq ? (
              <>
                <div>
                  <Label>Denumire echipament</Label>
                  <Input data-testid="service-log-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="ex: Hilti TE 70" />
                </div>
                <div>
                  <Label>Seria echipamentului</Label>
                  <Input data-testid="service-log-serial" value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} placeholder="ex: SN-123456" />
                </div>
              </>
            ) : (
              <div>
                <Label>Autoturismul (din platformă)</Label>
                <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                  <SelectTrigger data-testid="service-log-car"><SelectValue placeholder="Alege mașina…" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {cars.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {[c.marca, c.model].filter(Boolean).join(" ")} {c.numar_inmatriculare ? `· ${c.numar_inmatriculare}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notă / text</Label>
              <Textarea data-testid="service-log-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="ex: dus la reparat pe 12.08, garanție până în 2027…" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center justify-center gap-1.5 px-2 py-2.5 rounded border border-dashed border-zinc-300 hover:border-amber-500 cursor-pointer text-xs font-bold">
                📷 Fă poză
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="service-log-camera" />
              </label>
              <label className="flex items-center justify-center gap-1.5 px-2 py-2.5 rounded border border-dashed border-zinc-300 hover:border-amber-500 cursor-pointer text-xs font-bold">
                🖼 Din galerie
                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="service-log-gallery" />
              </label>
            </div>
            {photo && <div className="text-xs text-emerald-700 font-semibold">✓ {photo.name} <button type="button" className="text-red-500 ml-2" onClick={() => setPhoto(null)}>șterge</button></div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button data-testid="service-log-save" onClick={submit} disabled={saving || (isEq ? !form.title.trim() : !form.car_id)}>
                {saving ? "Se salvează…" : "Salvează"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!rows ? (
        <div className="text-sm text-zinc-400 py-8 text-center">Se încarcă…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-400 border border-dashed border-zinc-200 rounded p-8 text-center" data-testid="service-log-empty">
          Nicio înregistrare încă. Apasă „Adaugă" pentru primul echipament sau autoturism.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className={`card-flat p-4 border-l-4 ${r.status === "resolved" ? "border-l-emerald-500 opacity-70" : r.entry_type === "autoturism" ? "border-l-blue-500" : "border-l-amber-500"}`} data-testid={`service-log-row-${r.id}`}>
              <div className="flex items-start gap-3">
                {r.storage_path ? (
                  <a href={photoUrl(r.id)} target="_blank" rel="noreferrer" className="shrink-0">
                    <img src={photoUrl(r.id)} alt="" className="w-16 h-16 rounded-md object-cover border border-zinc-200" />
                  </a>
                ) : (
                  <div className="w-16 h-16 rounded-md bg-zinc-100 grid place-items-center text-2xl shrink-0">{r.entry_type === "autoturism" ? "🚗" : "🛠"}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{r.title}</span>
                    {r.plate && <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">{r.plate}</span>}
                    {r.serial && <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">SN: {r.serial}</span>}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${r.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {r.status === "resolved" ? "✓ REZOLVAT" : r.entry_type === "autoturism" ? "LA SERVICE" : "ÎN GARANȚIE"}
                    </span>
                  </div>
                  {r.note && <div className="text-sm text-zinc-600 mt-1">📝 {r.note}</div>}
                  <div className="text-[11px] text-zinc-400 mt-1">👤 {r.created_by} · {fmtRoDateTime(r.created_at)}</div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => toggle(r)} data-testid={`service-log-toggle-${r.id}`}>
                    {r.status === "resolved" ? "Redeschide" : "✓ Rezolvat"}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(r)} data-testid={`service-log-delete-${r.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
